import { html, css } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import { ViewBase } from '../view-base.js';
import { agreementViewStyles } from './agreement-view-styles.js';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/format-date/format-date.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/copy-button/copy-button.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/animation/animation.js';
import 'zero-md';

import {
  AgreementManager, signatureFonts, nextStepsByType,
} from '../../modules/agreement-manager.js';
import { Q1SpinnerPanel } from '../../components/q1-spinner-panel/q1-spinner-panel.js';
import { FormHostMixin } from '../../modules/form-host-mixin.js';
import { pollUntil } from '../../modules/polling-helpers.js';


export class AgreementView extends FormHostMixin(ViewBase) {
  static styles = [
    ...super.styles,
    agreementViewStyles,
  ];

  static properties = {
    ...super.properties,

    agreementId: { type: String },
    agreement: { type: Object },
    agreementStatusTitle: { type: String },
    responseDataFieldPairs: { type: Array },
    
    memberNumber: { type: String },
    nameLegal: { type: String },
    signatureFontKey: { type: String },
    signatureFontFamily: { type: String },
    responseData: { type: Object },
    legallyBound: { type: Boolean },
    declineReason: { type: String },

    loading: { type: Boolean },
    isError: { type: Boolean },
    errorMessage: { type: String },
    
    agreementManager: { type: Object },
    publicKey: { type: String },
    keyPairGeneratedAt: { type: Object },
    keyPairGenerating: { type: Boolean },
  };

  constructor() {
    super();
    this.agreementId = null;
    this.agreement = null;
    this.agreementStatusTitle = 'Agreement Loading';
    this.responseDataFieldPairs = [];

    this.memberNumber = null;
    this.nameLegal = null;
    this.signatureFontKey = signatureFonts[0][1];
    this.signatureFontFamily = signatureFonts[0][0];
    this.responseData = {};
    this.legallyBound = false;
    this.declineReason = '';

    this.loading = true;
    this.isError = false;
    this.errorMessage = null;
    
    this.agreementManager = null;
    this.publicKey = null;
    this.keyPairGeneratedAt = null;
    this.keyPairGenerating = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this._setupFonts();

    this.agreementId = this.routeParams?.key?.trim();
    if (!this.agreementId) {
      this.errorMessage = 'No agreement id provided';
      this.isError = true;
      this.loading = false;
    } else {
      this.updateComplete.then(() => {
        this.setAfLink_db('agreements', this.agreementId);
      });

      this.appElement.refreshCurrentMember().then((currentMember) => {
        this._initAgreementManager(currentMember);
      });
    }
  }
  
  firstUpdated() {
    document.addEventListener('scroll', () => this._handleScroll());
    this._handleScroll(); // do it once on initial render
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('currentMember')) {
      this._processCurrentMember();
    }
    if (changedProperties.has('signatureFontFamily')) {
      this.signatureFontKey = signatureFonts
        .find(([family, key]) => family === this.signatureFontFamily)[1];
    }

    // Listen for enter keypress in the dialog and map it to save (safe to run multiple times)
    this.registerEnterListenerOnDialog('approve-dialog', () => this.confirmApprove());
    this.registerEnterListenerOnDialog('decline-dialog', () => this.confirmDecline());
  }

  async _processCurrentMember() {
    if (this.currentMember?.id) {
      this.memberNumber = this.currentMember.number || '';
      this.nameLegal = this.currentMember.nameLegal || '';
      this.signatureFontFamily = this.currentMember.signatureFont || signatureFonts[0][0];
    }
  }

  /**
   * Inits agreement manager AND queries for the agreement.
   * This is run after a current member refresh so that the flags are up to date.
   * It should only run once.
   * Use `refreshAgreement()` to re-query after initial page load.
   */
  _initAgreementManager(currentMember) {
    this.agreementManager = new AgreementManager(currentMember);

    // This can happen in a side thread, doesn't need to block this one.
    this.agreementManager.initComplete.then(async () => {
      this.keyPairGeneratedAt = this.agreementManager.keyPair.generatedAt;
      this.publicKey = await this.agreementManager.exportPublicKey();
    }).catch((error) => {
      console.error('Error initializing agreement manager:', error);
      this.errorMessage = `Error initializing agreement manager: ${error.message}`;
      this.isError = true;
    });
  
    this.refreshAgreement();
  }

  _handleScroll() {
    const topThreshold = 1000;
    const bottomThreshold = this.scrollHeight - 2000;

    const upButton = this.renderRoot.getElementById('fab-up-button');
    const downButton = this.renderRoot.getElementById('fab-down-button');
    if (!upButton || !downButton) return;

    if (upButton.disabled && (window.scrollY > topThreshold)) upButton.disabled = false;
    if (!upButton.disabled && (window.scrollY < topThreshold)) upButton.disabled = true;
    
    if (downButton.disabled && (window.scrollY < bottomThreshold)) downButton.disabled = false;
    if (!downButton.disabled && (window.scrollY > bottomThreshold)) downButton.disabled = true;
  }

  _setupFonts() {
    if (document.body.dataset.signatureFontsLoaded) return;

    signatureFonts.forEach(([family, key]) => {
      const fontLink = document.createElement('link');
      fontLink.href =
        `https://fonts.googleapis.com/css2?family=${key}:wght@400&display=swap`;
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);
    });
    document.body.dataset.signatureFontsLoaded = true;
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  scrollToBottom() {
    this.scrollToCenterOf('response-footer');
  }

  _showToast_agreementManagerNotReady() {
    this.showToast(
      'The agreement manager couldn\'t setup your cryptographic keys. Please try refreshing. '
      + 'If the problem persists, please let the foundation team know.',
      'Something isn\'t working right.',
      'danger',
      30000
    );
  }

  async refreshKeyPair() {
    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    this.keyPairGenerating = true;

    try {
      await this.agreementManager.generateNewKeyPair();
      this.publicKey = this.agreementManager.keyPair.publicKey;
      this.keyPairGeneratedAt = this.agreementManager.keyPair.generatedAt;

      this.showToast('New cryptographic key pair generated.', 'Key Generation Complete', 'success');
    } catch (error) {
      console.error('Error generating new key pair:', error);
      this.showToast(error.message, 'Key Generation Failed', 'danger');
    } finally {
      this.keyPairGenerating = false;
    }
  }

  /** Optionally pass dialogId to close that dialog first. */
  showKeyMgmt(event, dialogId) {
    event.preventDefault();

    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    if (dialogId) this.renderRoot.getElementById(dialogId).hide();
    this.renderRoot.getElementById('key-mgmt-dialog').show();
  }

  closeKeyMgmt() {
    this.renderRoot.getElementById('key-mgmt-dialog').hide();
  }

  async refreshKeyPair() {
    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    if (!confirm(
      'Please confirm that you would like to generate a new key pair. '
      + 'Doing so will overwrite your current key pair and change your public signature.\n\n'
      + 'All of your previous signatures will still be valid. '
      + 'Rotating keys on a regular basis is a good security practice.'
    )) return true;

    this.loading = true;

    try {
      await this.agreementManager.generateNewKeyPair();
      this.keyPairGeneratedAt = this.agreementManager.keyPair.generatedAt;
      this.publicKey = await this.agreementManager.exportPublicKey();

      this.showToast('New cryptographic key pair generated.', 'Key Generation Complete', 'success');
    } catch (error) {
      console.error('Error generating new key pair:', error);
      this.showToast(error.message, 'Key Generation Failed', 'danger');
    } finally {
      this.loading = false;
    }
  }

  showApprove() {
    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    const responseForm = this.renderRoot.getElementById('response-form');
    if (!responseForm.reportValidity()) {
      this.showToast(
        'Complete all required form fields before approving the agreement.', null, 'danger'
      );
      return;
    }

    this.renderRoot.getElementById('approve-dialog').show();
  }
  
  closeApprove() {
    this.renderRoot.getElementById('approve-dialog').hide();
  }
  
  confirmApprove() {
    this.submitResponse('approved');
    this.renderRoot.getElementById('approve-dialog').hide();
  }

  showDecline() {
    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    // NOTE: We don't require that the response form be filled out for declined status.
    this.renderRoot.getElementById('decline-dialog').show();
  }
  
  closeDecline() {
    this.renderRoot.getElementById('decline-dialog').hide();
  }
  
  confirmDecline() {
    const declineForm = this.renderRoot.getElementById('decline-form'); 

    if (!declineForm.reportValidity()) {
      this.showToast(
        'Please enter a reason before declining the agreement.', null, 'danger'
      );
      return;
    }

    this.submitResponse('declined');
    this.renderRoot.getElementById('decline-dialog').hide();
  }

  async submitResponse(responseStatus) {
    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    const responseForm = this.renderRoot.getElementById('response-form');
    if ((responseStatus === 'approved') && !responseForm.reportValidity()) return;
    
    this.loading = true;
    
    try {
      await this.agreementManager.submitResponse({
        agreementId: this.agreementId,
        sentBody: this.agreement?.bodies?.sent?.content,
        status: responseStatus,
        memberNumber: this.memberNumber,
        nameLegal: this.nameLegal,
        signatureFont: this.signatureFontFamily,
        data: this.responseData,
        legallyBound: this.legallyBound,
        declineReason: this.declineReason,
      });

      this.showToast(
        'Processing and refreshing the agreement now...', 'Response submitted!', 'success'
      );
      
      // Reload agreement to refresh the view / potentially render the new status
      // NOTE: We leaving loading set for now, it will get cleared by refreshAgreement()
      await this.refreshAgreement();
      
      // Trigger a current member refresh to get any updates to the flags
      this.dispatchEvent(new CustomEvent('refresh-current-member', {
        bubbles: true, composed: true,
      }));

      this.scrollToTop();
      this.showToast('Latest version of agreement loaded.', 'Agreement Refreshed!', 'success');
    } catch (error) {
      console.error('Error submitting response:', error);
    
      if (/Signature cannot be verified/i.test(error.message) && this.isSafari){
        this.showToast(
          `Safari doesn’t yet support the cryptographic signatures we use to provide tamper proof 
          audit trails for all member agreements. We’re working on an alternate solution, but in 
          the meantime, please use Chrome or Firefox to e-sign the agreement.`,
          'e-Signing not yet supported in Safari',
          'danger',
          20000
        );
      } else {
        this.showToast(error.message, 'Failed to submit response', 'danger');
      }
      
      this.loading = false;
    }
  }

  /** Run after this.agreement is successfully refreshed. */
  _agreementPostProcessing() {
    this.setViewMeta(`${this.agreement.title || 'Agreement Detail'} - q1 Network`);

    this.agreementStatusTitle = this.agreement.statusTitleCase + ' Agreement';

    // If there are any response data fields, we want to pair them up so that we can use field
    // pairs to render them in the UI.
    // Go from [a, b, c, d, e] to [[a, b], [c, d], [e]]
    this.responseDataFieldPairs = [];
    const fields = this.agreement.type.responseDataFields
    for (let i = 0; i < fields.length; i += 2) {
      this.responseDataFieldPairs.push(fields.slice(i, i + 2));
    }
  }

  async refreshAgreement() {
    this.loading = true;

    try {
      this.agreement = await this.agreementManager.getAgreement(this.agreementId);
      
      if (this.agreement.processing) {
        // Poll until the agreement is done processing (or until maxDuration is reached)
        const isProcessingComplete = await pollUntil({
          isComplete: async () => {
            this.agreement = await this.agreementManager.getAgreement(this.agreementId);
            return !this.agreement.processing;
          },
        });

        if (!isProcessingComplete) throw new Error(
          'Agreement processing took too long. Please refresh the page to try again. '
          + ' If the problem persists, please contact the foundation team.'
        );
      }
      
      this._agreementPostProcessing();
    } catch (error) {
      console.error('Error loading agreement:', error);
      this.agreementStatusTitle = 'Agreement Load Error';
      this.errorMessage = error.message || 'Failed to load agreement';
      this.isError = true;
    } finally {
      this.loading = false;
    }
  }

  async deleteAgreement() {
    if (!this.agreementManager.ready) return this._showToast_agreementManagerNotReady();

    if (
      !confirm('Are you sure you want to delete this agreement? This action cannot be undone.')
    ) return;

    this.loading = true;

    try {
      await this.agreementManager.deleteMyAgreement(this.agreementId);

      this.showToast('Agreement successfully deleted.', 'Success', 'success');
      this.navigateTo('/home');
    } catch (error) {
      console.error('Error deleting agreement:', error);
      this.showToast(error.message, 'Failed to delete agreement', 'danger');
    } finally {
      this.loading = false;
    }
  }

  downloadPdf() {
    const { path: pdfUrl } = this.agreement.pdf || {};
    if (!pdfUrl) {
      console.log('downloadPdf: PDF path missing');
      return;
    }

    window.open(pdfUrl, '_blank').focus();
  }

  downloadJson() {
    let currentAgreementBody;
    let agreementBodies = this.agreement.bodies;

    if (this.agreement.status !== 'approved') {
      this.showToast('Only approved agreements can be downloaded as JSON.', null, 'danger');
      return;
    }
    
    currentAgreementBody = agreementBodies.approvedJson || {};
    if (!Object.keys(currentAgreementBody).length) {
      this.showToast('No approved body found to download.', null, 'danger');
      return;
    }

    const jsonStr = JSON.stringify(currentAgreementBody);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    
    const filename = this.agreement.pdf?.filename
      ? this.agreement.pdf?.filename.split('.')[0] + '.json'
      : `${new Date().toISOString().split('T')[0]} - Agreement.json`

    const anchorLink = document.createElement('a');
    anchorLink.href = url;
    anchorLink.download = filename;
    document.body.appendChild(anchorLink);
    anchorLink.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchorLink);
  }

  handleFontSelect(event) {
    event.preventDefault();
    
    const menuItemValue = event.detail?.item?.value;

    this.signatureFontFamily = menuItemValue;
  }

  handleDownloadSelect(event) {
    event.preventDefault();
    
    const menuItemValue = event.detail?.item?.value;

    switch (menuItemValue) {
      case 'pdf':
        this.downloadPdf();
        break;

      case 'json':
        this.downloadJson();
        break;
            
      default:
        console.log(`INVALID menu select item value: ${menuItemValue}`);
    }
  }

  get metaContext() {
    return { agreement: this.agreement };
  }  

  /** Returns the correct markdown content for the agreement based on its current status. */
  get currentBodyMarkdownContent() {
    if (!this.agreement?.bodies) return '';

    switch (this.agreement.status) {
      case 'draft':
      case 'sent':
        return this.agreement.bodies?.sent?.content || '';
      case 'approved':
        return this.agreement.bodies?.approved?.content || '';
      case 'declined':
        return this.agreement.bodies?.declined?.content || '';
      case 'canceled':
      case 'expired':
      default:
        return '';
    }
  }

  get contentTemplate() {
    return html`
      <header-bar>
        ${this.contentHeaderTemplate}
      </header-bar>

      <sl-divider></sl-divider>

      ${this.isError ? this.errorAlertTemplate : null}

      ${this.contentBodyTemplate}

      <q1-spinner-panel
        label=${this.agreement?.processing ? 'Agreement is processing...' : 'Loading...'}
        ?open=${this.loading}
      ></q1-spinner-panel>
    `;
  }

  get contentHeaderTemplate() {
    return html`
      ${this.agreement?.title ? html`
        <h1>
          <span class="prefix">${this.agreementStatusTitle}</span>
          ${this.agreement.title}
        </h1>

        ${(
          (this.agreement.status !== 'approved')
          || (!this.agreement.pdf?.path && !this.agreement.bodies?.approvedJson)
        ) ? null : html`
          <sl-dropdown id="download-dropdown" hoist>
            <sl-button slot="trigger" caret>
              <sl-icon slot="prefix" library="material" name="download"></sl-icon>  
              Download Agreement
            </sl-button>

            <sl-menu @sl-select=${this.handleDownloadSelect}>
              ${!this.agreement.pdf?.path ? null : html`
                <sl-menu-item value="pdf">
                  <sl-icon slot="prefix" library="material" name="picture_as_pdf"></sl-icon>
                  Download PDF
                </sl-menu-item>
              `}

              ${!this.agreement.bodies?.approvedJson ? null : html`
                <sl-menu-item value="json">
                  <sl-icon slot="prefix" library="material" name="file_json"></sl-icon>
                  Download JSON
                </sl-menu-item>
              `}
            </sl-menu>
          </sl-dropdown>
        `}

        ${this.agreement.status !== 'declined' ? null : html`
          <sl-button @click=${this.deleteAgreement}>
            <sl-icon slot="prefix" library="material" name="delete"></sl-icon>  
            Delete Agreement
          </sl-button>
        `}
      ` : html`
        <h1>${this.agreementStatusTitle}</h1>
      `}
    `;
  }

  get contentBodyTemplate() {
    if (this.loading) return this.loadingTemplate;
    if (!this.agreement?.status) return this.emptyTemplate;
    
    return this.agreementTemplate;
  }

  get errorAlertTemplate() {
    return html`
      <sl-alert variant="danger" open>
        <sl-icon library="material" name="report" slot="icon"></sl-icon>
        <strong>An error was encountered</strong><br/>
        ${this.errorMessage}
      </sl-alert>
    `;
  }

  get loadingTemplate() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="sync"></sl-icon>

          <h2>Agreement loading...</h2>

          <p>
            Please wait, your view will update shortly.
          </p>
        </div>
      </notice-panel>
    `;
  }

  /**
   * Returns the template for an empty agreement or an agreement which is somehow missing
   * its body content.
   */
  get emptyTemplate() {
    const titleText = this.agreement?.id
      ? 'Hmmm... something\'s not right'
      : 'Nothing to see here...';

    const bodyText = this.agreement?.id
      ? 'The agreement body content appears to be missing. Please contact the foundation team.'
      : 'Check the link and try again?';

    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="sentiment_dissatisfied"></sl-icon>

          <h2>${titleText}</h2>

          <p>
            ${bodyText}
          </p>
        </div>
      </notice-panel>
    `;
  }

  get agreementTemplate() {
    switch (this.agreement.status) {
      case 'sent':
        if (!this.agreement.bodies?.sent?.content) return this.emptyTemplate;
        if (this.agreement.cmResponseType === 'no-response') return this.sentSignerTemplate;
        if (this.agreement.cmResponseType === 'response') return this.sentSignerDoneTemplate;

        return this.sentNonSignerTemplate;
      case 'approved':
        if (!this.agreement.pdf?.path) return this.emptyTemplate;

        return this.approvedTemplate;
      case 'declined':
        if (!this.agreement.bodies?.declined?.content) return this.emptyTemplate;

        return this.closedTemplate;
      case 'canceled':
      case 'draft':
      case 'expired':
      default:
        return this.unsupportedStatusTemplate;
    }
  }

  get isSafari() {
    const ua = navigator.userAgent;
    return (
      /safari/i.test(ua) &&
      /version/i.test(ua) &&
      !/crios|fxios|edgios|opr\/|chrome/i.test(ua)
    );
  }
  

  get broswerCompatibilityTemplate() {
    return html`
      <sl-alert variant="danger" open>
        <sl-icon slot="icon" library="material" name="warning"></sl-icon>
        <strong>e-Signing not yet supported in Safari</strong><br/>
        Safari doesn’t yet support the cryptographic signatures we use to provide tamper proof 
        audit trails for all member agreements. We’re working on an alternate solution, but in the 
        meantime, please use Chrome or Firefox to e-sign the agreement.
      </sl-alert>
    `;
  }

  get sentSignerTemplate() {
    return html`
      <sl-alert variant="primary" open>
        <sl-icon slot="icon" library="material" name="description"></sl-icon>
        <strong>Please review and sign</strong><br/>
        Review the agreement below and provide your signature to proceed.
      </sl-alert>
      ${this.isSafari ? this.broswerCompatibilityTemplate : null}

      ${this.agreementMarkdownTemplate}
      ${this.signerResponseFooter}

      ${this.approveDialog}
      ${this.declineDialog}
      ${this.keyManagementDialog}

      ${this.isSafari ? this.broswerCompatibilityTemplate : null}
      
      ${this.scrollFabsTemplate}
    `;
  }
  
  get sentSignerDoneTemplate() {
    return html`
      <sl-alert variant="success" open>
        <sl-icon slot="icon" library="material" name="check_circle"></sl-icon>
        <strong>You've already responded to this agreement</strong><br/>
        On
        <sl-format-date
          date=${this.agreement.cmResponse?.respondedAt}
          month="short" day="numeric" year="numeric"
        ></sl-format-date>
        you responded with
        <strong>${this.agreement.cmResponse?.status}</strong>.
      </sl-alert>

      ${this.agreementMarkdownTemplate}

      ${this.signerResponseFooter}
      
      ${this.scrollFabsTemplate}
    `;
  }

  get sentNonSignerTemplate() {
    return html`
      <sl-alert variant="neutral" open>
        <sl-icon slot="icon" library="material" name="schedule"></sl-icon>
        <strong>Signing in progress</strong><br/>
        This agreement is currently awaiting signatures.
        You are not one of the signers.
      </sl-alert>

      ${this.agreementMarkdownTemplate}

      ${this.scrollFabsTemplate}
    `;
  }

  get approvedTemplate() {
    return html`
      ${this.agreement.nextSteps ? this.nextStepsTemplate : html`
        <sl-alert variant="success" open>
          <sl-icon slot="icon" library="material" name="check_circle"></sl-icon>
          <strong>Agreement is approved</strong><br/>
          This agreement has been fully executed and was approved on
          <sl-format-date
            date=${this.agreement.approvedAt}
            month="short" day="numeric" year="numeric"
          ></sl-format-date>.
        </sl-alert>
      `}
  
      ${this.agreement.pdf?.path ? html`
        <div class="pdf-embed">
          <embed 
            type="application/pdf"
            src="${this.agreement.pdf.path}"
            class="pdf-viewer"
            title="${this.agreement.pdf.filename}"
          >
        </div>
      ` : html`
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" library="material" name="error"></sl-icon>
          PDF version of this agreement is not yet available.
        </sl-alert>
      `}
    `;
  }
  
  get closedTemplate() {
    return html`
      <sl-alert variant="danger" open>
        <sl-icon slot="icon" library="material" name="cancel"></sl-icon>
        <strong>Agreement is ${this.agreement.status}</strong><br/>
        This agreement was closed on
        <sl-format-date
          date=${this.agreement.closedAt}
          month="short" day="numeric" year="numeric"
        ></sl-format-date>.

        ${!this.agreement.declineReason ? null : html`
          <br/>
          Decline Reason: &ldquo;${this.agreement.declineReason}&rdquo;
        `}
      </sl-alert>

      ${this.agreementMarkdownTemplate}
      
      ${this.scrollFabsTemplate}
    `;
  }

  get unsupportedStatusTemplate() {
    return html`
      <notice-panel class="hero">
        <div class="inner">
          <sl-icon library="material" name="report"></sl-icon>

          <h2>Hmmm... something's not right</h2>

          <p>
            This agreement somehow got to an invalid status (${this.agreement.status}).
            Please contact the foundation team.
          </p>
        </div>
      </notice-panel>
    `;
  }

  get nextStepsTemplate() {
    return html`
      <sl-animation name="pulse" easing="ease" duration="1500" play>
        <notice-bar class="primary">
          <span class="content">${this.agreement.nextSteps.message}</span>

          <sl-button
            variant="text"
            href=${this.agreement.nextSteps.ctaPath}
          >
            ${this.agreement.nextSteps.ctaText}
            <sl-icon slot="suffix" library="material" name="arrow_circle_right"></sl-icon>
          </sl-button>
        </notice-bar>
      </sl-animation>
    `;
  }

  get signerResponseFooter() {
    return html`
      <response-footer>
        <div class="inner">
          <p class="subtitle">
            Please use the form below to respond to the agreement.
            <br class="hide-until-bp2"/>
            Your response will be 
            <a href="#" @click=${this.showKeyMgmt}>cryptographically signed</a>
            to provide a verifiable audit trail.
          </p>

          <form id="response-form" class="q1">
            <signature-block>
              <sl-input
                id="nameLegal"
                name="nameLegal"
                label="Your Legal Name"
                size="medium"
                value=${this.nameLegal}
                @sl-input=${this.handleInput}
                class="signature"
                style=${styleMap({ '--signature-font': this.signatureFontFamily })}
                required
              >
                <sl-icon slot="prefix" library="material" name="signature"></sl-icon>
              </sl-input>
              
              <sl-dropdown id="font-dropdown">
                <sl-button slot="trigger" size="small" outline caret>Font</sl-button>
                <sl-menu @sl-select=${this.handleFontSelect}>
                  ${signatureFonts.map(([family, key]) => html`
                    <sl-menu-item
                      value=${family}
                      class="signature"
                      style=${`--signature-font: '${family}';`}
                    >
                      ${(family !== this.signatureFontFamily) ? null : html`
                        <sl-icon slot="prefix" library="material" name="check"></sl-icon>
                      `}
                      ${this.nameLegal}
                    </sl-menu-item>
                  `)}
                </sl-menu>
              </sl-dropdown>
            </signature-block>

            ${!this.responseDataFieldPairs?.length ? null : html`
              <!-- IMPROVEME: Add support for other field formats -->
              ${this.responseDataFieldPairs.map(([field1, field2]) => html`
                <field-pair>
                  <sl-input
                    name=${`responseData.${field1.key}`}
                    label=${field1.label}
                    size="medium"
                    @sl-change=${this.handleChange}
                    class="response-data-field"
                    ?required=${field1.required}
                  ></sl-input>
                  
                  ${field2?.key ? html`
                    <sl-input
                      name=${`responseData.${field2.key}`}
                      label=${field2.label}
                      size="medium"
                      @sl-change=${this.handleChange}
                      class="response-data-field"
                      ?required=${field2.required}
                    ></sl-input>
                  ` : html`
                    <field-spacer></field-spacer>
                  `}
                </field-pair>
              `)}
            `}

            <field-pair>
              <sl-input
                id="agreementDate"
                label="Agreement Date"
                type="date"
                size="medium"
                value=${this.agreement.agreementDate}
                disabled
              ></sl-input>

              <sl-checkbox
                id="legallyBound"
                name="legallyBound"
                size="medium"
                @sl-change=${this.handleChecked}
                required
              >I agree to be legally bound by this agreement</sl-checkbox>
            </field-pair>
          </form>
          
          <button-bar>
            <sl-button
              id="rf-decline-button"
              @click=${this.showDecline}
              size="large"
            >
              <sl-icon slot="prefix" library="material" name="close"></sl-icon>
              Decline
            </sl-button>

            <span class="crypto-info">
              <sl-button
                variant="default"
                outline
                @click=${this.showKeyMgmt}
                ?disabled=${this.loading}
              >
                <sl-icon slot="prefix" library="material" name="encrypted"></sl-icon>
                Cryptographically signed
              </sl-button>
            </span>

            <sl-button
              id="rf-approve-button"
              @click=${this.showApprove}
              variant="success"
              size="large"
            >
              <sl-icon slot="prefix" library="material" name="check"></sl-icon>
              Approve
            </sl-button>
          </button-bar>
        </div>
      </response-footer>
    `;
  }

  get scrollFabsTemplate() {
    return html`
      <scroll-fab class="up">
        <sl-button
          id="fab-up-button"
          variant="warning"
          @click=${this.scrollToTop}
          size="large"
          pill
          disabled
        >
          <sl-icon slot="prefix" library="material" name="arrow_upward"></sl-icon>
          Scroll to Top
          <sl-icon slot="suffix" library="material" name="arrow_upward"></sl-icon>
        </sl-button>
      </scroll-fab>

      <scroll-fab class="down">
        <sl-button
          id="fab-down-button"
          variant="warning"
          @click=${this.scrollToBottom}
          size="large"
          pill
          disabled
        >
          <sl-icon slot="prefix" library="material" name="arrow_downward"></sl-icon>
          Scroll to Bottom
          <sl-icon slot="suffix" library="material" name="arrow_downward"></sl-icon>
        </sl-button>
      </scroll-fab>
    `;
  }

  get agreementMarkdownTemplate() {
    return html`
      <zero-md class=${this.agreement.status}>
        <script type="text/markdown">${this.currentBodyMarkdownContent}</script>
        <template data-append>
          <style>
            /* IMPROVEME: Update for v1 to support dynamic styling based on current signer */

            .markdown-body {
              background: white;
              color: var(--gray-9);
              padding: 1.5rem;
              --bgColor-default: white;
              --bgColor-muted: white;
              --borderColor-default: var(--gray-6);
              --borderColor-muted: var(--gray-2);
            }
            @media(min-width: 650px) {
              .markdown-body {
                padding: 2rem;
              }
            }
            @media(min-width: 1000px) {
              .markdown-body {
                padding: 2.5rem;
              }
            }

            .markdown-body hr {
              background-color: var(--borderColor-muted);
              height: 2px;
              margin: var(--view-item-spacing) 0;
            } 

            .markdown-body p {
              text-align: justify;
            }
            .markdown-body p:has(field) {
              line-height: 1.6rem;
            }

            .markdown-body field {
              display: inline-block;
              border-bottom: var(--border-bottom);
              padding: 0.4rem 1rem 0.2rem 1rem;
              font-size: 0.9em;
              line-height: 1em;
              font-weight: 300;
              color: var(--color);
              background: var(--background);

              --color: var(--gray-8);
              --background: var(--gray-1);
              --border-bottom: 1px solid var(--gray-9);
            }
            .markdown-body field.signer0 {
              --background: var(--highlight-background);
              --border-bottom: 1px solid var(--highlight-color);
            }

            .markdown-body signature {
              display: inline-block;
              border-bottom: var(--border-bottom);
              padding: 1rem 1rem;
              font-size: 1.5rem;
              line-height: 1em;
              font-weight: 300;
              color: var(--color);
              background: var(--background);

              --color: var(--gray-8);
              --background: var(--gray-1);
              --border-bottom: 1px solid var(--gray-9);
            }
            .markdown-body signature.signer0 {
              --background: var(--highlight-background);
              --border-bottom: 1px solid var(--highlight-color);
            }
            .markdown-body signature.approved {
              font-size: 2rem;
              font-family: var(--signature-font, 'Cedarville Cursive');
              font-weight: 400;
              padding-bottom: 0;
            }
            .markdown-body signature.declined {
              --background: var(--red-1);
              --color: var(--red-8);
              --border-bottom: 1px solid var(--red-9);
            }

            header-bar {
              display: flex;
              gap: 1rem;
              align-items: center;
              font-size: 1.1rem;
              font-weight: 300;
              color: var(--gray-6);
              font-style: italic;
              margin-bottom: 2rem;
            }
            header-bar img {
              height: 1.5rem;
            }

            hash {
              font-family: 'Courier New', Courier, monospace;
              background-color: var(--gray-2);
              padding: 0.2em 0.4em;
              border-radius: 3px;
              font-size: 0.9em;
            }
          </style>
        </template>
      </zero-md>
    `;
  }

  get approveDialog() {
    return html`
      <sl-dialog
        id="approve-dialog"
        label="Agreement Approval Confirmation"
      >
        <div class="dialog-content">
          <p>
            By clicking "Confirm Approval" (or pressing Enter)
            you are agreeing to be legally bound by this agreement.
          </p>

          <p>
            <small>
              Your response will be cryptographically signed using the private key 
              stored securely in your browser. To view & manage your keys,
              <a href="#" @click=${(e) => this.showKeyMgmt(e, 'approve-dialog')}>click here</a>.
            </small>
          </p>
        </div>

        <button-bar slot="footer">
          <sl-button
            variant="default"
            @click="${this.closeApprove}"
          >Close</sl-button>

          <sl-button variant="success" @click=${this.confirmApprove}>
            <sl-icon slot="prefix" library="material" name="check"></sl-icon>
            Confirm Approval
          </sl-button>
        </button-bar>
      </sl-dialog>
    `;
  }

  get declineDialog() {
    return html`
      <sl-dialog
        id="decline-dialog"
        label="Agreement Decline Confirmation"
      >
        <div class="dialog-content">
          <p>
            <strong>
              By clicking "Confirm Decline" you are formally declining the 
              agreement and you will not be legally bound by it.
            </strong>
          </p>

          <form id="decline-form">
            <sl-textarea
              id="declineReason"
              name="declineReason"
              label="Reason for Declining"
              @sl-change=${this.handleChange}
              rows="3"
              maxlength="500"
              help-text="Please share some more context on why you are declining the agreement"
              required
              autofocus
            ></sl-textarea>
          </form>

          <p>
            <small>
              Your response will be cryptographically signed using the private key 
              stored securely in your browser. To view & manage your keys,
              <a href="#" @click=${(e) => this.showKeyMgmt(e, 'decline-dialog')}>click here</a>.
            </small>
          </p>
        </div>

        <button-bar slot="footer">
          <sl-button
            variant="default"
            @click="${this.closeDecline}"
          >Close</sl-button>

          <sl-button variant="danger" @click=${this.confirmDecline}>
            <sl-icon slot="prefix" library="material" name="check"></sl-icon>
            Confirm Decline
          </sl-button>
        </button-bar>
      </sl-dialog>
    `;
  }

  get keyManagementDialog() {
    return html`
      <sl-dialog id="key-mgmt-dialog" label="Key Management">
        <div class="dialog-content">
          <p>
            All of the agreements you respond to in the q1 Network are cryptographically signed 
            with your private key. This ensures that your responses are secure and independently 
            verifiable.
          </p>

          <p>
            <strong>Your Cryptographic Key Pair</strong>
          </p>

          <p>
            Your Public Key:
            <sl-copy-button from="public-key"></sl-copy-button>
            <br/>
            <code id="public-key" class="hash">${this.publicKey}</code>
          </p>

          <p>
            Key Pair Generated:
            <sl-format-date
              .date=${this.keyPairGeneratedAt}
              month="short" day="numeric" year="numeric"
            ></sl-format-date>
          </p>

          <sl-details summary="Additional Info">
            <p>
              Your private key is securely stored in your browser using the
              <a
                href="https://developer.mozilla.org/en-US/docs/Web/API/Web_Crypto_API"
                target="_blank"
              >Web Crypto API</a>.
              It is non-extractable meaning that while it can be used to sign messages,
              no one (not even you) can extract it from the browser.
              This ensures that your private key remains secure and cannot be compromised.
            </p>

            <p>
              Every time you respond to an agreement, the agreement and
              your response are converted into a string which is then signed with your private key.
              This resulting signed hash is then stored with the agreement, providing a
              cryptographic proof that cannot be altered or forged by current technology.
            </p>
  
            <p>
              If you would like to refresh your key pair, which is a good security practice to do
              on a regular basis, you can click the button below.
              This will generate a new key pair and replace the existing one.
            </p>
          </sl-details>

          <sl-details summary="Technical Specs">
            <p>
              Hashes are computed using SHA-256. Key pairs are generated via ECDSA on the P-256 
              (secp256r1) curve, with the private key designated as non-exportable for enhanced 
              security. Key material is securely stored in the browser's IndexedDB.
            </p>
            <p>
              If you'd like to inspect the code used to generate and manage the keys and sign 
              messages, find the <code>/src/modules/crypto-helpers.js</code> file in your
              browser's dev tools.
            </p>
            <p>
              Open source references and an independent verification mechanism are forthcoming.
              Holochain & blockchain integrations are also planned, to support an array of 
              peer-to-peer & decentralized verification mechanisms.
            </p>
            <p>
              If you have any questions or concerns about the cryptographic security of the q1 
              Network, or if you'd like to help us improve it, please reach out to the
              foundation team.
            </p>
          </sl-details>

          <q1-spinner-panel
            label="Refreshing..."
            ?open=${this.keyPairGenerating}
          ></q1-spinner-panel>

        </div>

        <button-bar slot="footer">
          <sl-button variant="primary" @click=${this.refreshKeyPair}>
            <sl-icon slot="prefix" library="material" name="refresh"></sl-icon>
            Refresh Key Pair
          </sl-button>

          <sl-button
            variant="default"
            @click="${this.closeKeyMgmt}"
          >Close</sl-button>
        </button-bar>
      </sl-dialog>
    `;
  }
}

customElements.define('agreement-view', AgreementView);